"use client";

import { useEffect, useRef } from "react";
import { Globe, Loader2, Send } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import VoiceCommentInput from "@/components/voice/VoiceCommentInput";
import { cn } from "@/lib/utils";
import { SABINE_SOLID, SABINE_SOLID_HOVER } from "@/components/nav/nav-styles";

/** A bare round icon control in the row (web search, the mic). */
export const COMPOSER_GHOST_BUTTON_CLASS =
  "h-9 w-9 shrink-0 rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800";

const SEND_CLASS = cn("h-9 w-9 shrink-0 rounded-full", SABINE_SOLID, SABINE_SOLID_HOVER);

/**
 * The Sabine chat's message row: a white field on the pane's cream, holding
 * the text, the optional web search toggle, the mic and the send button.
 * Enter sends (`onKeyDown` comes from `useSubmit`); `autoFocus` puts the
 * caret in the field when the chat opens, so typing continues from the pill
 * that opened it.
 *
 * @param {object} props
 * @param {React.Ref} props.formRef             `useSubmit`'s form ref
 * @param {(event: SubmitEvent, options: {enableWebSearch: boolean}) => void} props.onSubmit
 * @param {string} props.inputValue
 * @param {(event: {target: {value: string}}) => void} props.onInputChange
 * @param {Function} [props.setInputValue]      Lets dictation append to the draft
 * @param {(event: KeyboardEvent) => void} props.onKeyDown
 * @param {string} props.placeholder
 * @param {boolean} props.isProcessing
 * @param {boolean} [props.autoFocus]
 * @param {boolean} [props.showWebSearchToggle]
 * @param {boolean} [props.enableWebSearch]
 * @param {() => void} [props.onToggleWebSearch]
 */
export default function AIChatComposer({
  formRef,
  onSubmit,
  inputValue,
  onInputChange,
  setInputValue,
  onKeyDown,
  placeholder,
  isProcessing,
  autoFocus = false,
  showWebSearchToggle = false,
  enableWebSearch = true,
  onToggleWebSearch,
}) {
  const inputRef = useRef(null);
  const canSend = !isProcessing && Boolean(inputValue?.trim());
  const webSearchLabel = enableWebSearch
    ? "Web search enabled - AI can search the internet"
    : "Web search disabled - AI will only use playbook content";

  // preventScroll: beside the rail the composer is focused as Sabine's column
  // starts to widen, and a plain focus() scrolls that overflow-hidden column
  // sideways to reveal it, so the chat jolts mid-slide (search does the same).
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const appendDictation = (text) => {
    if (setInputValue) {
      setInputValue((prev) => prev + (prev ? " " : "") + text);
      return;
    }
    onInputChange({
      target: { value: inputValue + (inputValue ? " " : "") + text },
    });
  };

  return (
    <form
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        // Enter reaches here even when the send button is disabled.
        if (!canSend) return;
        onSubmit(event, { enableWebSearch });
      }}
      className="shrink-0 px-3 pb-3 pt-2"
      data-testid="ai-chat-form"
    >
      <div className="flex w-full items-end gap-1.5 rounded-xl border border-stone-200/70 bg-white py-1.5 pl-2.5 pr-1.5 focus-within:border-stone-300">
        <TextareaAutosize
          ref={inputRef}
          name="coach-input"
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid="ai-chat-input"
          className="min-w-0 flex-1 resize-none self-center bg-transparent py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          disabled={isProcessing}
          minRows={1}
          maxRows={6}
        />

        {showWebSearchToggle && (
          <SimpleTooltip content={webSearchLabel}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onToggleWebSearch}
              disabled={isProcessing}
              aria-label={webSearchLabel}
              className={cn(
                COMPOSER_GHOST_BUTTON_CLASS,
                enableWebSearch ? "text-slate-800" : "text-slate-300",
              )}
            >
              <Globe className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
        )}

        <VoiceCommentInput
          onTranscriptionComplete={appendDictation}
          onPartialTranscription={() => {}}
          disabled={isProcessing}
          compact={true}
          showDropdownIndicator={false}
          variant="ghost"
        />

        <SimpleTooltip content="Send message">
          <Button
            type="submit"
            size="icon"
            aria-label="Send message"
            data-testid="ai-chat-submit"
            disabled={!canSend}
            className={cn(SEND_CLASS, "disabled:opacity-40")}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </SimpleTooltip>
      </div>
    </form>
  );
}
